import React, { useState } from "react";
import { Container } from "@mui/material";
import toast from 'react-hot-toast';
import Header from "../../components/Header/Header";
import api from "../../config/apiConfig";
import AppLoader from "../../components/AppLoader/AppLoader";

const Bonds: React.FC = () => {
  const [files, setFiles] = useState<FileList | null>(null);
  const [fileNames, setFileNames] = useState<string[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(false);

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(e.target.files);
    }
  };

const handleFileSelect = (selectedFiles: FileList) => {
    if (selectedFiles.length !== 2) {
      toast.error("Please select exactly two files. On macOS, hold Command (⌘) to select multiple files.");
      return;
    }

    const validFiles = Array.from(selectedFiles).filter(file =>
      ['.xls', '.xlsx', '.csv'].includes(file.name.slice(file.name.lastIndexOf('.')))
    );

    if (validFiles.length !== 2) {
      toast.error("Both files must be .xls, .xlsx, or .csv.");
      return;
    }

    setFiles(selectedFiles);
    setFileNames(validFiles.map(file => file.name));
  };

  const handleSubmit = async () => {
     if (!files || files.length !== 2) {
      toast.error("Please select exactly two files to upload.");
      return;
    }

    const formData = new FormData();
for (const file of Array.from(files)) {
    formData.append("file", file);
  }
    formData.append("listingType", "bond");
      setLoading(true);
    try {
      const accessToken = localStorage.getItem("accessToken");
      if (!accessToken) {
        toast.error("Authentication token not found.");
        return;
      }

      const response = await api.post(
        "admin/uploadListingData",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `${accessToken}`,
          },
        }
      );
      setLoading(false);
      toast.success("Files uploaded successfully.");
      console.log(response.data);
      setFiles(null);
      setFileNames([]);
      setRefreshKey(prevKey => prevKey + 1);
    } catch (error) {
      console.error("Error uploading files:", error);
      toast.error("Error uploading files. Please check file types or try again.");
    }
  };

  const handleChangeFile = () => {
    setFiles(null);
    setFileNames([]);
  };

  return (
    <>
          <AppLoader loading={loading} />
      <Container
        key={refreshKey}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "70vh",
          textAlign: "center",
        }}
      >
        <div
          className="flex flex-col items-center justify-center w-[60%] h-auto"
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div
            className={`w-full h-full flex flex-col items-center justify-center cursor-pointer ${
              dragActive ? "bg-gray-100" : "bg-gray-50"
            }`}
          >
            <div className="flex items-center justify-center w-full">
              <label
                htmlFor="dropzone-file"
                className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-5 hover:bg-gray-10"
              >
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <svg
                    className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 20 16"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                    />
                  </svg>
                  <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                    <span className="font-semibold">Click to select</span> or drag and drop multiple files
                    {/* <br />
                    <span className="text-xs">(On macOS, hold Command (⌘) to select multiple files)</span> */}
                  </p>
                </div>
                <input
                  id="dropzone-file"
                  type="file"
                  className="hidden"
                  accept=".xls,.xlsx,.csv"
                  multiple
                  onChange={handleChange}
                />
              </label>
            </div>
          </div>
          {fileNames.length > 0 && (
            <div className="mt-4 text-gray-700">
              <strong>Selected Files:</strong>
              <div className="mt-2">
                <p><strong>File 1:</strong> {fileNames[0]}</p>
                <p><strong>File 2:</strong> {fileNames[1]}</p>
              </div>
            </div>
          )}
          <div className="mt-4 flex space-x-4">
            {files && files.length > 0 && (
              <button
                onClick={handleChangeFile}
                className="px-4 py-2 bg-gray-300 text-black rounded hover:bg-gray-400"
              >
                Cancel
              </button>
            )}
            {fileNames.length ===0 && (
              <label
                htmlFor="file-input"
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 cursor-pointer"
              >
                Browse Files
              </label>
            )}
            <input
              id="file-input"
              type="file"
              className="hidden"
              accept=".xls,.xlsx,.csv"
              multiple
              onChange={handleChange}
            />
            {files && files.length === 2 && (
              <button
                onClick={handleSubmit}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Upload
              </button>
            )}
          </div>
        </div>
      </Container>
    </>
  );
};

export default Bonds;